import type { IDateValue } from "../types/common";
import type { CardProps } from "@mui/material/Card";
import type { TableHeadCustomProps } from "../components/table";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import TableRow from "@mui/material/TableRow";
import { useTheme } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import IconButton from "@mui/material/IconButton";
import CardHeader from "@mui/material/CardHeader";
import ListItemText from "@mui/material/ListItemText";
import Badge, { badgeClasses } from "@mui/material/Badge";
import Typography from "@mui/material/Typography";

import { fCurrency } from "../utils/format-number";
import { fDate, fTime } from "../utils/format-time";

import { Label } from "../components/label";
import { Iconify } from "../components/iconify";
import { Scrollbar } from "../components/scrollbar";
import { TableHeadCustom } from "../components/table";
import { usePopover, CustomPopover } from "../components/custom-popover";
import React from "react";
import { IStoreState, useAppDispatch, useAppSelector } from "../../../redux";
import { useNavigate } from "react-router-dom";
import {
  clearPaymentReceivedStateSync,
  clearPaymentsPaidState,
  fetchPaymentPaidListWithArgsAsync,
  IPaymentPaid,
  IPaymentReceived,
} from "../../../redux/accounting";
import { StandardCard } from "../../../Components/Card";
import { CustomChip } from "../../../Components/CustomChip";
import { Chip } from "@mui/material";

// ----------------------------------------------------------------------

type Props = CardProps & {
  title?: string;
  subheader?: string;
  // headLabel: TableHeadCustomProps['headLabel'];
  tableData: {
    id: string;
    type: string;
    status: string;
    amount: number;
    message: string;
    date: IDateValue;
    category: string;
    name: string | null;
    avatarUrl: string | null;
  }[];
};

export function BankingRecentTransitions({
  title,
  subheader,
  tableData,
  // headLabel,
  ...other
}: Props) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: payments_paid_list, loading } = useAppSelector(
    (storeState: IStoreState) =>
      storeState.accounting.paymentPaid.payments_paid_list
  );
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";
  React.useEffect(() => {
    dispatch(
      fetchPaymentPaidListWithArgsAsync({
        page: 1,
        rowsPerPage: 10,
      })
    );

    return () => {
      dispatch(clearPaymentsPaidState());
    };
  }, []);

  const handleOnViewAllUsersClick = () => {
    navigate(`/accounting/payment-paid`);
  };

  return (
    <StandardCard
      heading={title}
      rightHeading={
        <Button
          size="small"
          color="inherit"
          sx={{
            backgroundColor: isLightMode ? "var(--secondary-color)" : "var(--primary-color)",
            color: isLightMode ? "var(--text-light-color)" : "var(--text-dark-color)",
            border: isLightMode ? "1px solid var(--border-light-color)" : "1px solid var(--border-dark-color)",
            "&:hover": {
              backgroundColor: isLightMode ? "var(--secondary-color)" : "var(--tertiary-color)",
              opacity: isLightMode ? 0.85 : 1,
            }
          }}
          onClick={handleOnViewAllUsersClick}
          endIcon={
            <Iconify
              icon="eva:arrow-ios-forward-fill"
              width={18}
              sx={{ ml: -0.5 }}
            />
          }
        >
          View all
        </Button>
      }
    >
      <Table
        sx={{
          minWidth: 720,
          position: "relative",
          "& td": {
            whiteSpace: "nowrap",
          },
        }}
      >
        <TableBody>
          {payments_paid_list.map((row) => (
            <RowItem key={row.payment_paid_uuid} row={row} />
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ borderStyle: "dashed" }} />

      <Box sx={{ p: 2, textAlign: "right" }}>
        <Button
          size="small"
          color="inherit"
          sx={{
            backgroundColor: isLightMode ? "var(--secondary-color)" : "var(--primary-color)",
            color: isLightMode ? "var(--text-light-color)" : "var(--text-dark-color)",
            border: isLightMode ? "1px solid var(--border-light-color)" : "1px solid var(--border-dark-color)",
            "&:hover": {
              backgroundColor: isLightMode ? "var(--secondary-color)" : "var(--tertiary-color)",
              opacity: isLightMode ? 0.85 : 1,
            }
          }}
          endIcon={
            <Iconify
              icon="eva:arrow-ios-forward-fill"
              width={18}
              sx={{ ml: -0.5 }}
            />
          }
        >
          View all
        </Button>
      </Box>
    </StandardCard>
  );
}

// ----------------------------------------------------------------------

type RowItemProps = {
  row: IPaymentPaid;
};

function RowItem({ row }: RowItemProps) {
  const theme = useTheme();
  const popover = usePopover();

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
        return {
          bg: "rgba(84, 214, 44, 0.16)",
          color: "rgb(34, 154, 22)",
        };
      case "pending":
        return {
          bg: "rgba(255, 193, 7, 0.16)",
          color: "rgb(183, 129, 3)",
        };
      case "cancelled":
      case "failed":
        return {
          bg: "rgba(255, 72, 66, 0.16)",
          color: "rgb(183, 33, 54)",
        };
      default:
        return {
          bg: "rgba(145, 158, 171, 0.16)",
          color: "rgb(99, 115, 129)",
        };
    }
  };

  const statusColors = getStatusColor(row.order_status);

  return (
    <>
      <TableRow
        sx={{
          "& td": { border: 0, py: 1.5 },
          position: "relative",
          "&:hover": {
            backgroundColor: "rgba(145, 158, 171, 0.08)",
          },
          cursor: "pointer",
        }}
      >
        <TableCell
          sx={{
            width: "100%",
            pr: 7,
            pl: 3,
          }}
        >
          <Box>
            {/* Order ID and Status Row */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "text.primary",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  {row.order_number}
                </Typography>
                <Chip
                  label={row.order_status}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    borderRadius: "6px",
                    textTransform: "capitalize",
                    backgroundColor: statusColors.bg,
                    color: statusColors.color,
                    border: "none",
                  }}
                />
              </Box>
              <IconButton
                color={popover.open ? "inherit" : "default"}
                onClick={(e) => {
                  e.stopPropagation();
                  popover.onOpen(e);
                }}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.6,
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              >
                <Iconify icon="eva:more-vertical-fill" />
              </IconButton>
            </Box>

            {/* Order Details Row */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  fontSize: "1rem",
                }}
              >
                {row.vendor ? `${row.vendor} •` : ""}
                {row.items_count ? `${row.items_count} items  •` : ""}
              </Typography>
              <Typography>
                {row.total_amount ? `$${row.total_amount}  •` : ""}{" "}
              </Typography>
              <Typography variant="caption">
                {row.create_ts
                  ? `${fDate(row.create_ts)} at ${fTime(row.create_ts)}  •`
                  : ""}
              </Typography>
              {/* <Typography variant="caption">•</Typography> */}
              <Typography variant="caption">
                {row.customer_name ? `${row.vendor}  •` : ""}
              </Typography>

              <Typography variant="caption">
                Pickup #{row.pickup_number ? `${row.order_number}  •` : ""}
              </Typography>
            </Box>

            {/* Notes Row */}
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mb: 0.5,
                fontSize: "0.75rem",
                fontStyle: row.notes ? "normal" : "italic",
              }}
            >
              {row.notes || "No special instructions"}
            </Typography>

            {/* Meta Information Row */}
          </Box>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: "right-top" } }}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: theme.shadows[5],
            borderRadius: 1.5,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e1e1e" : "white",
          },
        }}
      >
        <MenuList>
          <MenuItem
            onClick={() => {
              popover.onClose();
              console.info("DOWNLOAD", row.payment_paid_uuid);
            }}
          >
            <Iconify icon="eva:cloud-download-fill" sx={{ mr: 2 }} />
            Download
          </MenuItem>

          <MenuItem
            onClick={() => {
              popover.onClose();
              console.info("PRINT", row.payment_paid_uuid);
            }}
          >
            <Iconify icon="solar:printer-minimalistic-bold" sx={{ mr: 2 }} />
            Print
          </MenuItem>

          <MenuItem
            onClick={() => {
              popover.onClose();
              console.info("SHARE", row.payment_paid_uuid);
            }}
          >
            <Iconify icon="solar:share-bold" sx={{ mr: 2 }} />
            Share
          </MenuItem>

          <Divider sx={{ borderStyle: "dashed" }} />

          <MenuItem
            onClick={() => {
              popover.onClose();
              console.info("DELETE", row.payment_paid_uuid);
            }}
            sx={{ color: "error.main" }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" sx={{ mr: 2 }} />
            Delete
          </MenuItem>
        </MenuList>
      </CustomPopover>
    </>
  );
}
